import React from 'react';
import { Button, ProfessionalDateRange } from '../../../shared/components';
import { Filter, Search, X } from 'lucide-react';
import { getCurrentYear } from '../../../shared/utils/eventStatus';
import { QUEUE_STATUSES, DELIVERY_STATUSES, NOTIFICATION_TYPES } from './notificationTypes';

const SELECT_CLASS =
  'w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white';

/**
 * Filtros: año, estado de cola, estado de entrega, tipo, teléfono y rango de fechas.
 */
const NotificationsFilters = ({
  filters = {},
  onFiltersChange,
  onClearFilters,
  onSearch,
  onYearChange,
  availableYears = [],
  loading = false
}) => {
  const handleChange = (field) => (e) => onFiltersChange({ [field]: e.target.value });

  const handleDateRangeChange = (startDate, endDate) => {
    onFiltersChange({ startDate: startDate || '', endDate: endDate || '' });
  };

  // El año vigente es el valor por defecto: no cuenta como filtro activo
  const currentYear = getCurrentYear();
  const hasActiveFilters = Object.entries(filters).some(([key, value]) =>
    key === 'year' ? value !== currentYear : Boolean(value)
  );

  const yearOptions = Array.from(
    new Set([...availableYears.map(String), currentYear, ...(filters.year ? [String(filters.year)] : [])])
  ).sort((a, b) => Number(b) - Number(a));

  return (
    <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-200/30">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
            <Filter className="w-5 h-5 text-blue-600" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 tracking-tight">Filtros</h3>
        </div>

        {hasActiveFilters && (
          <Button onClick={onClearFilters} variant="outline" size="sm" disabled={loading} className="text-gray-600">
            <X className="w-4 h-4 mr-2" />
            Limpiar Filtros
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        <div className="xl:col-span-2">
          <ProfessionalDateRange
            startDate={filters.startDate}
            endDate={filters.endDate}
            onChange={handleDateRangeChange}
            disabled={loading}
            label="Periodo de Consulta"
            className="w-full"
          />
        </div>

        <div className="flex flex-col">
          <label className="block text-sm font-medium text-gray-700 mb-2">Año</label>
          <select
            value={filters.year || ''}
            onChange={(e) => onYearChange(e.target.value)}
            disabled={loading}
            className={SELECT_CLASS}
          >
            <option value="">Todos los años</option>
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col">
          <label className="block text-sm font-medium text-gray-700 mb-2">Estado en la cola</label>
          <select value={filters.status || ''} onChange={handleChange('status')} disabled={loading} className={SELECT_CLASS}>
            <option value="">Todos</option>
            {QUEUE_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col">
          <label className="block text-sm font-medium text-gray-700 mb-2">Estado de entrega</label>
          <select value={filters.deliveryStatus || ''} onChange={handleChange('deliveryStatus')} disabled={loading} className={SELECT_CLASS}>
            <option value="">Todos</option>
            {DELIVERY_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col">
          <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de mensaje</label>
          <select value={filters.type || ''} onChange={handleChange('type')} disabled={loading} className={SELECT_CLASS}>
            <option value="">Todos</option>
            {NOTIFICATION_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col">
          <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono</label>
          <input
            type="text"
            value={filters.recipientPhone || ''}
            onChange={handleChange('recipientPhone')}
            onKeyDown={(e) => e.key === 'Enter' && onSearch()}
            placeholder="Buscar por número..."
            disabled={loading}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
        </div>
      </div>

      <div className="flex justify-end mt-6">
        <Button onClick={onSearch} disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white">
          <Search className="w-4 h-4 mr-2" />
          {loading ? 'Buscando...' : 'Aplicar Filtros'}
        </Button>
      </div>
    </div>
  );
};

export default NotificationsFilters;
